
import { supabase } from '@/lib/supabase';
import { MessageType } from '@/types/assistant';
import { logger } from '@/utils/production-logger';
import { DeduplicationService } from './database/DeduplicationService';
import { MessagePersistenceService } from './database/MessagePersistenceService';

export class DatabaseStreamingService {
  static async loadMessagesWithStreamingValidation(
    sessionId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<MessageType[]> {
    try {
      const { data: messagesData, error } = await supabase
        .from('assistant_messages')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true })
        .range(offset, offset + limit - 1);

      if (error) {
        logger.error('Database error loading messages', {
          error: error.message,
          sessionId,
          component: 'DatabaseStreamingService'
        });
        throw error;
      }

      if (!messagesData) {
        return [];
      }

      const validatedMessages: MessageType[] = messagesData.map(msg => {
        const message: MessageType = {
          id: msg.id,
          content: msg.content || '',
          role: msg.role,
          timestamp: new Date(msg.created_at),
          session_id: msg.session_id,
          user_id: msg.user_id,
          suggestions: Array.isArray(msg.suggestions) ? msg.suggestions : [],
          is_streaming: false, // Database messages are never streaming
          model: msg.model || 'gpt-4',
          feedback: msg.feedback || null,
          metadata: msg.metadata || {}
        };

        return message;
      });

      logger.debug('Messages loaded with validation', {
        sessionId,
        count: validatedMessages.length,
        component: 'DatabaseStreamingService'
      });

      return validatedMessages;
    } catch (error) {
      logger.error('Error loading messages', {
        error: error instanceof Error ? error.message : String(error),
        sessionId,
        component: 'DatabaseStreamingService'
      });
      return [];
    }
  }

  static async saveMessageWithStreamingValidation(message: MessageType): Promise<boolean> {
    try {
      // STEP 1: Check if this message is already being saved using DeduplicationService
      if (DeduplicationService.isMessageSaveActive(message.id)) {
        console.log('[DEDUP] Message save already in progress:', { messageId: message.id });
        return await DeduplicationService.getActiveSave(message.id)!;
      }

      // STEP 1: Create save promise and track it using DeduplicationService
      const savePromise = MessagePersistenceService.saveMessageToDatabase(message);
      DeduplicationService.trackSave(message.id, savePromise);

      try {
        const result = await savePromise;
        return result;
      } finally {
        // STEP 1: Clean up tracking when done
        DeduplicationService.cleanupSave(message.id);
      }
    } catch (error) {
      // STEP 1: Clean up tracking on error
      DeduplicationService.cleanupSave(message.id);
      throw error;
    }
  }
}
